import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, Printer, Download, ArrowLeft, Car, User, IndianRupee,
  Calendar, Phone, Mail, MapPin, Building, Edit3, Check, X, Save,
  History, Eye, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.round(amount));
};

const formatNumber = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(amount);
};

function ProformaInvoice({ user, onLogout }) {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [proforma, setProforma] = React.useState(null);
  const [sourceData, setSourceData] = React.useState(null);
  
  // Saved proformas for this lead/booking
  const [savedProformas, setSavedProformas] = React.useState([]);
  const [showSavedProformas, setShowSavedProformas] = React.useState(false);
  
  // Vehicle selection for proforma
  const [vehiclePrices, setVehiclePrices] = React.useState([]);
  const [selectedVehicle, setSelectedVehicle] = React.useState(null);
  const [showVehicleSelector, setShowVehicleSelector] = React.useState(false);
  
  // Customization options
  const [customizations, setCustomizations] = React.useState({
    custom_insurance: null,
    custom_accessories: null,
    custom_discount: null,
    include_affidavit: false,
    include_special_number: false,
    include_out_of_city: false,
    affidavit_amount: 500,
    special_number_amount: 0,
    out_of_city_amount: 0,
    notes: ''
  });
  
  // Company data
  const [company, setCompany] = React.useState(null);

  // Fetch data on mount
  React.useEffect(() => {
    fetchInitialData();
  }, [type, id]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Fetch vehicle prices
      const pricesRes = await axios.get(`${BACKEND_URL}/api/masters/vehicle-prices`, getAuthHeader());
      setVehiclePrices(pricesRes.data || []);

      // Fetch company info from masters
      try {
        const mastersRes = await axios.get(`${BACKEND_URL}/api/masters?type=company`, getAuthHeader());
        if (mastersRes.data && mastersRes.data.length > 0) {
          setCompany(mastersRes.data[0]);
        }
      } catch (e) {
        console.log('Company info not available');
      }

      // If we have type and id, fetch source data (lead or booking)
      if (type && id) {
        // Fetch saved proformas for this lead/booking
        try {
          const savedRes = await axios.get(
            `${BACKEND_URL}/api/proforma-invoice/${type}/${id}`, 
            getAuthHeader()
          );
          setSavedProformas(savedRes.data || []);
        } catch (e) {
          console.log('No saved proformas found');
        }

        if (type === 'lead') {
          const leadRes = await axios.get(`${BACKEND_URL}/api/leads/${id}`, getAuthHeader());
          setSourceData(leadRes.data);
          
          // Auto-select vehicle if brand/model/variant match
          const matchingPrice = pricesRes.data?.find(p => 
            p.brand_name === leadRes.data.vehicle_brand &&
            p.model === leadRes.data.vehicle_model &&
            p.variant === leadRes.data.vehicle_variant
          );
          if (matchingPrice) {
            setSelectedVehicle(matchingPrice);
            generateProforma(matchingPrice, leadRes.data);
          } else {
            setShowVehicleSelector(true);
          }
        } else if (type === 'booking') {
          const bookingRes = await axios.get(`${BACKEND_URL}/api/bookings/${id}`, getAuthHeader());
          setSourceData(bookingRes.data);
          
          // Auto-select vehicle if brand/model/variant match
          const matchingPrice = pricesRes.data?.find(p => 
            p.brand_name === bookingRes.data.brand_name &&
            p.model === bookingRes.data.model &&
            p.variant === bookingRes.data.variant
          );
          if (matchingPrice) {
            setSelectedVehicle(matchingPrice);
            generateProforma(matchingPrice, bookingRes.data);
          } else {
            setShowVehicleSelector(true);
          }
        }
      } else {
        // No source - show vehicle selector
        setShowVehicleSelector(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const generateProforma = async (vehicle, source = sourceData) => {
    if (!vehicle) {
      toast.error('Please select a vehicle');
      return;
    }
    
    setGenerating(true);
    try {
      const payload = {
        brand_name: vehicle.brand_name,
        model: vehicle.model,
        variant: vehicle.variant,
        color: vehicle.color || source?.color || source?.vehicle_color || '',
        customer_name: source?.customer_first_name 
          ? `${source.salutation || ''} ${source.customer_first_name} ${source.customer_last_name || ''}`.trim()
          : source?.first_name 
            ? `${source.salutation || ''} ${source.first_name} ${source.last_name || ''}`.trim()
            : '',
        customer_mobile: source?.mobile_no_1 || source?.mobile || '',
        customer_email: source?.email || '',
        customer_address: [
          source?.door_no || source?.address_door_no,
          source?.address_1,
          source?.address_2,
          source?.area,
          source?.city,
          source?.state,
          source?.pin_code
        ].filter(Boolean).join(', '),
        include_insurance: true,
        include_mandatory_accessories: true,
        include_registration: true,
        include_handling: true,
        include_b2b: true,
        include_bpp: true,
        include_rsa: true,
        include_pme_drive: true,
        include_baas: true,
        include_price_benefits: true,
        include_discounts: true,
        include_affidavit: customizations.include_affidavit,
        include_special_number: customizations.include_special_number,
        include_out_of_city: customizations.include_out_of_city,
        custom_insurance: customizations.custom_insurance,
        custom_accessories: customizations.custom_accessories,
        custom_discount: customizations.custom_discount,
        affidavit_amount: customizations.affidavit_amount,
        special_number_amount: customizations.special_number_amount,
        out_of_city_amount: customizations.out_of_city_amount,
        notes: customizations.notes
      };
      
      const response = await axios.post(
        `${BACKEND_URL}/api/proforma-invoice/generate`,
        payload,
        getAuthHeader()
      );
      
      setProforma(response.data);
      if (response.data.company) {
        setCompany(response.data.company);
      }
      setShowVehicleSelector(false);
    } catch (error) {
      console.error('Error generating proforma:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate proforma invoice');
    }
    setGenerating(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // Use browser print to PDF
    window.print();
    toast.success('Use "Save as PDF" in print dialog');
  };

  const handleSaveProforma = async () => {
    if (!proforma) {
      toast.error('No proforma to save');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        lead_id: type === 'lead' ? id : null,
        booking_id: type === 'booking' ? id : null,
        proforma_data: proforma
      };
      
      await axios.post(`${BACKEND_URL}/api/proforma-invoice/save`, payload, getAuthHeader());
      toast.success('Proforma invoice saved successfully');
      
      // Refresh saved proformas list
      if (type && id) {
        const savedRes = await axios.get(
          `${BACKEND_URL}/api/proforma-invoice/${type}/${id}`, 
          getAuthHeader()
        );
        setSavedProformas(savedRes.data || []);
      }
    } catch (error) {
      console.error('Error saving proforma:', error);
      toast.error(error.response?.data?.detail || 'Failed to save proforma invoice');
    }
    setSaving(false);
  };

  const handleViewSavedProforma = async (proformaId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/proforma-invoice/${proformaId}`,
        getAuthHeader()
      );
      setProforma(response.data);
      if (response.data.company) {
        setCompany(response.data.company);
      }
      setShowSavedProformas(false);
    } catch (error) {
      toast.error('Failed to load proforma');
    }
  };

  const handleCustomizationChange = (field, value) => {
    setCustomizations(prev => ({ ...prev, [field]: value }));
  };

  const handleRegenerate = () => {
    if (selectedVehicle) {
      generateProforma(selectedVehicle);
    }
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Proforma Invoice">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Proforma Invoice">
      <div className="max-w-6xl mx-auto p-4">
        {/* Header Actions - Hide on Print */}
        <div className="print:hidden mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-6 h-6 text-teal-600" />
                Proforma Invoice
              </h1>
              <p className="text-sm text-gray-500">
                {sourceData ? (type === 'lead' ? `Lead: ${sourceData.lead_no}` : `Booking: ${sourceData.booking_no}`) : 'Generate Quote'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* Show Saved Proformas Button (when linked to lead/booking) */}
            {type && id && savedProformas.length > 0 && (
              <button
                onClick={() => setShowSavedProformas(true)}
                className="flex items-center gap-2 px-4 py-2 border border-amber-400 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 text-sm font-medium"
                data-testid="view-saved-proformas-btn"
              >
                <History className="w-4 h-4" />
                Saved ({savedProformas.length})
              </button>
            )}
            {proforma && (
              <>
                {/* Save Button (only when linked to lead/booking) */}
                {type && id && (
                  <button
                    onClick={handleSaveProforma}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
                    data-testid="save-proforma-btn"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                )}
                <button
                  onClick={() => setShowVehicleSelector(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 text-sm font-medium"
                >
                  <Edit3 className="w-4 h-4" />
                  Change Vehicle
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-medium"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </button>
              </>
            )}
          </div>
        </div>

        {/* Saved Proformas Modal */}
        {showSavedProformas && (
          <div className="print:hidden fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Saved Proforma Invoices
                </h2>
                <button onClick={() => setShowSavedProformas(false)} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {savedProformas.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No saved proformas</p>
                ) : (
                  <div className="space-y-3">
                    {savedProformas.map((sp) => (
                      <div 
                        key={sp.id}
                        className="p-4 border rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-gray-800 flex items-center gap-2">
                              <FileText className="w-4 h-4 text-teal-600" />
                              {sp.invoice_number}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {sp.vehicle?.brand} {sp.vehicle?.model} - {sp.vehicle?.variant}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Created: {new Date(sp.created_at).toLocaleDateString('en-IN')} | 
                              Total: {formatCurrency(sp.grand_total)}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewSavedProforma(sp.id)}
                              className="p-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t bg-gray-50 flex justify-end">
                <button
                  onClick={() => {
                    setShowSavedProformas(false);
                    setShowVehicleSelector(true);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 text-sm font-medium"
                >
                  Create New Proforma
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Selector Modal */}
        {showVehicleSelector && (
          <div className="print:hidden fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b bg-gradient-to-r from-teal-600 to-emerald-600 text-white flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  Select Vehicle for Proforma
                </h2>
                {proforma && (
                  <button onClick={() => setShowVehicleSelector(false)} className="p-2 hover:bg-white/20 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {vehiclePrices.length === 0 ? (
                  <div className="text-center py-8">
                    <IndianRupee className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No vehicle prices configured</p>
                    <button
                      onClick={() => navigate('/sales/vehicle-price-master')}
                      className="mt-3 text-teal-600 hover:underline"
                    >
                      Configure Vehicle Prices
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {vehiclePrices.filter(p => p.is_active !== false).map(price => (
                      <button
                        key={price.id}
                        onClick={() => {
                          setSelectedVehicle(price);
                          generateProforma(price);
                        }}
                        className={`p-4 border-2 rounded-xl text-left transition-all hover:shadow-md ${
                          selectedVehicle?.id === price.id 
                            ? 'border-teal-500 bg-teal-50' 
                            : 'border-gray-200 hover:border-teal-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-gray-800">{price.brand_name}</h3>
                            <p className="text-sm text-gray-600">{price.model} - {price.variant}</p>
                            {price.color && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 rounded text-xs">{price.color}</span>
                            )}
                          </div>
                          {selectedVehicle?.id === price.id && (
                            <Check className="w-5 h-5 text-teal-600" />
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t">
                          <div className="text-xs text-gray-500">Ex-Showroom</div>
                          <div className="font-bold text-teal-700">{formatCurrency(price.ex_showroom_price)}</div>
                          <div className="text-xs text-gray-400 mt-1">On-Road: {formatCurrency(price.on_road_price)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Customization Options */}
              {selectedVehicle && (
                <div className="p-4 border-t bg-gray-50">
                  <h3 className="font-semibold text-gray-700 mb-3">Customize Invoice</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Custom Insurance</label>
                      <input
                        type="number"
                        value={customizations.custom_insurance || ''}
                        onChange={(e) => handleCustomizationChange('custom_insurance', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Default"
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Custom Accessories</label>
                      <input
                        type="number"
                        value={customizations.custom_accessories || ''}
                        onChange={(e) => handleCustomizationChange('custom_accessories', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Default"
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Custom Discount</label>
                      <input
                        type="number"
                        value={customizations.custom_discount || ''}
                        onChange={(e) => handleCustomizationChange('custom_discount', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Default"
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Special Number Amt</label>
                      <input
                        type="number"
                        value={customizations.special_number_amount}
                        onChange={(e) => handleCustomizationChange('special_number_amount', parseFloat(e.target.value) || 0)}
                        className="w-full p-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={customizations.include_affidavit}
                        onChange={(e) => handleCustomizationChange('include_affidavit', e.target.checked)}
                        className="rounded"
                      />
                      Include Affidavit (Rs.{customizations.affidavit_amount})
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={customizations.include_special_number}
                        onChange={(e) => handleCustomizationChange('include_special_number', e.target.checked)}
                        className="rounded"
                      />
                      Special Number
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={customizations.include_out_of_city}
                        onChange={(e) => handleCustomizationChange('include_out_of_city', e.target.checked)}
                        className="rounded"
                      />
                      Out of City Charges
                    </label>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs text-gray-600 mb-1">Additional Notes</label>
                    <textarea
                      value={customizations.notes}
                      onChange={(e) => handleCustomizationChange('notes', e.target.value)}
                      placeholder="Any special notes for this quote..."
                      className="w-full p-2 border rounded-lg text-sm"
                      rows={2}
                    />
                  </div>
                  <button
                    onClick={handleRegenerate}
                    disabled={generating}
                    className="mt-3 px-6 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:opacity-50"
                  >
                    {generating ? 'Generating...' : 'Regenerate Invoice'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Proforma Invoice Display */}
        {proforma && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden print:shadow-none print:rounded-none">
            {/* Company Letterhead */}
            <div className="p-6 border-b print:p-4">
              <div className="flex justify-between items-start gap-6">
                {/* Left: Company Details */}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide" data-testid="company-name">
                    {company?.name || proforma.company_name || 'Dealership'}
                  </h2>
                  {company?.hashtag && (
                    <p className="text-sm text-teal-600 font-medium italic mt-0.5">{company.hashtag}</p>
                  )}
                  
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    {(company?.door_no || company?.building_name || company?.address_1) && (
                      <p>
                        {[company?.door_no, company?.building_name, company?.address_1].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {(company?.area || company?.city || company?.state || company?.pincode) && (
                      <p>
                        {[company?.area, company?.city, company?.state, company?.pincode && `- ${company.pincode}`].filter(Boolean).join(', ')}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs text-gray-500">
                      {company?.mobile && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {company.mobile}
                          {company?.mobile_2 && ` / ${company.mobile_2}`}
                        </span>
                      )}
                      {company?.landline_1 && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {company.landline_1}
                        </span>
                      )}
                      {company?.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {company.email}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-xs text-gray-500">
                      {company?.gst_no && <span><strong>GSTIN:</strong> {company.gst_no}</span>}
                      {company?.uin_no && <span><strong>UIN:</strong> {company.uin_no}</span>}
                    </div>
                  </div>
                </div>

                {/* Right: Company Logo */}
                {company?.logo && (
                  <div className="flex-shrink-0" data-testid="company-logo">
                    <img
                      src={company.logo}
                      alt="Company Logo"
                      className="w-24 h-24 object-contain rounded-lg border border-gray-200 print:w-20 print:h-20"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Title Bar */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4 print:bg-slate-800 print:p-3">
              <div className="flex justify-between items-center">
                <h1 className="text-xl font-bold tracking-wide">PROFORMA INVOICE</h1>
                <div className="flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-slate-400">Invoice # </span>
                    <span className="font-semibold">{proforma.invoice_number || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Date: </span>
                    <span className="font-semibold">{proforma.invoice_date}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Valid Until: </span>
                    <span className="font-semibold">{proforma.valid_until}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="p-6 border-b">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Bill To</h3>
              <div className="bg-teal-50 rounded-xl p-4">
                <h4 className="font-bold text-gray-800 flex items-center gap-2">
                  <User className="w-4 h-4 text-teal-600" />
                  {proforma.customer?.name || 'Valued Customer'}
                </h4>
                {proforma.customer?.mobile && (
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {proforma.customer.mobile}
                  </p>
                )}
                {proforma.customer?.email && (
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {proforma.customer.email}
                  </p>
                )}
                {proforma.customer?.address && (
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {proforma.customer.address}
                  </p>
                )}
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="p-6 border-b bg-gradient-to-r from-teal-50 to-emerald-50">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Vehicle Details</h3>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Car className="w-8 h-8 text-teal-600" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-800">
                    {proforma.vehicle?.brand} {proforma.vehicle?.model}
                  </h4>
                  <p className="text-gray-600">
                    {proforma.vehicle?.variant}
                    {proforma.vehicle?.color && ` | ${proforma.vehicle.color}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="p-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Price Breakdown</h3>
              
              <div className="space-y-1">
                {proforma.line_items?.filter(item => item.included).map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`flex justify-between items-center py-2 px-3 rounded-lg ${
                      item.is_deduction 
                        ? 'bg-red-50 text-red-700' 
                        : item.description?.includes('Ex-Showroom') 
                          ? 'bg-blue-50' 
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className={`text-sm ${item.description?.includes('Ex-Showroom') ? 'font-semibold' : ''}`}>
                      {item.description}
                    </span>
                    <span className={`font-medium ${item.is_deduction ? 'text-red-700' : ''}`}>
                      {formatCurrency(Math.abs(item.amount))}
                    </span>
                  </div>
                ))}
              </div>

              {/* Total Section */}
              <div className="mt-6 pt-4 border-t-2 border-teal-200">
                <div className="flex justify-between items-center py-3 px-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl">
                  <div>
                    <span className="text-lg font-semibold">ON-ROAD PRICE</span>
                    {proforma.grand_total_words && (
                      <p className="text-xs text-white/80 mt-0.5">{proforma.grand_total_words}</p>
                    )}
                  </div>
                  <span className="text-2xl font-bold">{formatCurrency(proforma.grand_total)}</span>
                </div>
              </div>

              {/* Summary Boxes */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500">Ex-Showroom</div>
                  <div className="font-bold text-gray-800">{formatCurrency(proforma.subtotal)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500">Total GST</div>
                  <div className="font-bold text-gray-800">{formatCurrency(proforma.total_gst)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500">Other Charges</div>
                  <div className="font-bold text-gray-800">{formatCurrency(
                    proforma.line_items?.filter(i => i.included && !i.is_deduction && !i.description?.includes('Ex-Showroom') && !i.description?.includes('GST'))
                      .reduce((sum, i) => sum + i.amount, 0)
                  )}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-red-500">Deductions</div>
                  <div className="font-bold text-red-700">{formatCurrency(
                    Math.abs(proforma.line_items?.filter(i => i.is_deduction).reduce((sum, i) => sum + i.amount, 0) || 0)
                  )}</div>
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="p-6 bg-gray-50 border-t">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Terms & Conditions</h3>
              <div className="text-xs text-gray-600 space-y-1 whitespace-pre-line">
                {proforma.terms_and_conditions}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t text-center text-xs text-gray-400">
              This is a computer-generated proforma invoice and does not require a signature.
              <br />
              Prices are subject to change without prior notice. This is not a tax invoice.
            </div>
          </div>
        )}

        {/* Empty State */}
        {!proforma && !showVehicleSelector && (
          <div className="bg-white rounded-2xl border p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No Proforma Generated</h3>
            <p className="text-gray-500 mt-1">Select a vehicle to generate a proforma invoice</p>
            <button
              onClick={() => setShowVehicleSelector(true)}
              className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700"
            >
              Select Vehicle
            </button>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          .bg-white.rounded-2xl.shadow-lg,
          .bg-white.rounded-2xl.shadow-lg * {
            visibility: visible;
          }
          .bg-white.rounded-2xl.shadow-lg {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </SalesLayout>
  );
}

export default ProformaInvoice;
